import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Palette, 
  Mail, 
  Presentation, 
  Eye,
  FileText,
  X,
  ChevronLeft,
  ChevronRight,
  Grid,
  Filter
} from 'lucide-react';
import { getAllImages } from '../data/portfolioData';
import './Portfolio.css';

const Portfolio = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [filteredImages, setFilteredImages] = useState([]);
  const [displayCount, setDisplayCount] = useState(6);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && selectedImage) {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedImage]);

  const categories = [
    {
      id: 'all',
      title: 'Todos os Projetos',
      icon: <Grid size={20} />,
      color: '#6366f1'
    },
    {
      id: 'logos',
      title: 'Logotipos',
      icon: <Palette size={20} />,
      color: '#f59e0b',
      description: 'Identidades visuais e logotipos únicos'
    },
    {
      id: 'paper',
      title: 'Papelaria',
      icon: <FileText size={20} />,
      color: '#10b981',
      description: 'Materiais corporativos e papelaria'
    },
    {
      id: 'ebooks',
      title: 'E-books / E-mail Mkt',
      icon: <Mail size={20} />,
      color: '#8b5cf6',
      description: 'E-books e campanhas digitais'
    },
    {
      id: 'posts',
      title: 'Posts',
      icon: <Presentation size={20} />,
      color: '#ef4444',
      description: 'Posts para redes sociais'
    },
    {
      id: 'events',
      title: 'Comunicação Visual',
      icon: <Eye size={20} />,
      color: '#06b6d4',
      description: 'Materiais para eventos e comunicação'
    }
  ];

  useEffect(() => {
    setDisplayCount(6);
    const allImages = getAllImages();

    if (activeCategory === 'all') {
      setFilteredImages(allImages);
    } else {
      setFilteredImages(allImages.filter(img => img.category === activeCategory));
    }
    setSelectedImage(null);
    setCurrentImageIndex(0);
  }, [activeCategory]);

  const displayedImages = useMemo(() => {
    return filteredImages.slice(0, displayCount);
  }, [filteredImages, displayCount]);

  const openModal = (image, index) => {
    setSelectedImage(image);
    setCurrentImageIndex(index);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    setIsFilterOpen(false);
  };

  const toggleFilter = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const nextImage = () => {
    if (displayedImages.length === 0) return;
    const nextIndex = (currentImageIndex + 1) % displayedImages.length;
    setCurrentImageIndex(nextIndex);
    setSelectedImage(displayedImages[nextIndex]);
  };

  const prevImage = () => {
    if (displayedImages.length === 0) return;
    const prevIndex = currentImageIndex === 0 ? displayedImages.length - 1 : currentImageIndex - 1;
    setCurrentImageIndex(prevIndex);
    setSelectedImage(displayedImages[prevIndex]);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.05,
        staggerChildren: 0.06
      }
    }
  };

  const imageVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  return (
    <section id="portfolio" className="portfolio">
      <div className="container">
        {/* Header */}
        <motion.div
          className="portfolio-header"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <h2 className="section-title">
            <Filter size={20} />
            MEU PORTFÓLIO
          </h2>
          <p className="section-subtitle">
            Explore meus trabalhos organizados por categoria. Clique em qualquer imagem para visualizar em tamanho completo.
          </p>
        </motion.div>

        <div className="portfolio-toolbar">
        {/* Filtros de Categoria - Dropdown */}
        <motion.div
          className="portfolio-filters"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="filter-dropdown">
            <motion.button
              className="filter-trigger"
              onClick={toggleFilter}
              whileTap={{ scale: 0.98 }}
              aria-expanded={isFilterOpen}
            >
              <div className="trigger-content">
                <div className="trigger-icon">
                  {categories.find(cat => cat.id === activeCategory)?.icon}
                </div>
                <div className="trigger-text">
                  <span className="trigger-label">Filtros</span>
                  <span className="trigger-value">{categories.find(cat => cat.id === activeCategory)?.title}</span>
                </div>
              </div>
              <motion.div
                className="trigger-arrow"
                animate={{ rotate: isFilterOpen ? -90 : 90 }}
                transition={{ duration: 0.3 }}
              >
                <ChevronRight size={18} />
              </motion.div>
            </motion.button>

            <AnimatePresence>
              {isFilterOpen && (
                <motion.div
                  className="filter-dropdown-content"
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div className="dropdown-header">
                    <span>Escolha uma categoria</span>
                  </div>
                  <div className="dropdown-options">
                    {categories.map((category) => (
                      <motion.button
                        key={category.id}
                        className={`dropdown-option ${activeCategory === category.id ? 'active' : ''}`}
                        onClick={() => handleCategoryChange(category.id)}
                        whileTap={{ scale: 0.99 }}
                      >
                        <div className="option-icon">
                          {category.icon}
                        </div>
                        <div className="option-content">
                          <span className="option-title">{category.title}</span>
                          {category.description && (
                            <span className="option-description">{category.description}</span>
                          )}
                        </div>
                        {activeCategory === category.id && (
                          <motion.div
                            className="option-check"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                          >
                            ✓
                          </motion.div>
                        )}
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Contador de Imagens */}
        <motion.div
          className="portfolio-counter"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <span>{filteredImages.length} projeto{filteredImages.length !== 1 ? 's' : ''}</span>
        </motion.div>
        </div>

        {/* Grid de Imagens */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory}
            className="portfolio-grid"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {displayedImages.map((image, index) => (
              <motion.div
                key={`${image.src}-${index}`}
                className="portfolio-item"
                data-category={image.category}
                variants={imageVariants}
                onClick={() => openModal(image, index)}
              >
                <div className="image-container">
                  <img 
                    src={image.src} 
                    alt={image.title}
                    loading="lazy"
                  />
                  <div className="image-overlay">
                    <div className="overlay-content">
                      <h4>{image.title}</h4>
                      <span className="category-badge">
                        {categories.find(cat => cat.id === image.category)?.title}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Ver mais */}
        {filteredImages.length > displayedImages.length && (
          <div className="see-more-wrapper">
            <button
              className="see-more-btn"
              onClick={() => setDisplayCount(prev => Math.min(prev + 6, filteredImages.length))}
            >
              <svg viewBox="0 0 24 24" className="arr-2" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="text">Ver mais</span>
              <span className="circle"></span>
              <svg viewBox="0 0 24 24" className="arr-1" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        )}

        {/* Mensagem quando não há imagens */}
        {filteredImages.length === 0 && (
          <motion.div
            className="no-images"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Eye size={48} />
            <h3>Categoria em Desenvolvimento</h3>
            <p>Os projetos desta categoria serão adicionados em breve.</p>
          </motion.div>
        )}
      </div>

      {/* Modal de Visualização */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="portfolio-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div
              className="modal-content"
              initial={{ y: 16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 16, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className="modal-close" onClick={closeModal} aria-label="Fechar">
                <X size={20} />
              </button>

              {displayedImages.length > 1 && (
                <>
                  <button className="modal-nav prev" onClick={prevImage} aria-label="Anterior">
                    <ChevronLeft size={22} />
                  </button>
                  <button className="modal-nav next" onClick={nextImage} aria-label="Próxima">
                    <ChevronRight size={22} />
                  </button>
                </>
              )}

              <div className="modal-image">
                <img src={selectedImage.src} alt={selectedImage.title} />
              </div>

              <div className="modal-info">
                <h3>{selectedImage.title}</h3>
                <span className="modal-category">
                  {categories.find(cat => cat.id === selectedImage.category)?.title}
                </span>
                <div className="modal-counter">
                  {currentImageIndex + 1} / {displayedImages.length}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Portfolio;
